import { api } from '@udm/contracts';
import { useQuery } from '@tanstack/react-query';
import { CircleAlert, CircleCheck, Loader2, Pencil, Save, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError, request } from '../../api/client';
import { Card } from '../../ui/components';
import { sendCommand, settled } from './commands';
import { changes, editable, REPORT_LIST_MAX, same, toWire, validate, type Json, type Obj } from './readEdit';
import { bodyOf, ReadResult, TASK_SLOTS, useFieldLabel, ValueView, type EditState } from './ReadResult';

/** Reads offered by the quick buttons of the commands card. */
const QUICK = new Set(['telemetry.read', 'location.read']);

type Prop = { type?: string; enum?: unknown[]; minimum?: number; maximum?: number };
type Schema = { properties?: Record<string, Prop>; required?: string[] };
type Task = { task_no: number; time: string; dimming: number; period_min: number };
type Last = { key: string; params: Record<string, unknown>; r: api.DeviceQueryResponse };
type Op = { capability: string; params: Record<string, unknown> };
type Outcome = {
  failed?: { capability: string; code: string };
  /** per change: confirmed by the read-back, or the value read back */
  checks: { key: string; ok: boolean; read: Json }[];
};

/** Translated capability name; the catalog's English name when the key has no translation yet. */
export function useCapabilityLabel() {
  const { t } = useTranslation();
  return (key: string, fallback?: string) =>
    t(`catalog.capability.${key}` as 'catalog.capability.location.read', { defaultValue: fallback ?? key });
}

const taskList = (body: Obj): Task[] =>
  ((body.tasks as Obj[] | undefined) ?? [])
    .filter((t) => t && t.empty !== true)
    .map((t) => ({
      task_no: Number(t.task_no),
      time: String(t.time ?? ''),
      dimming: Number(t.dimming ?? 0),
      period_min: Number(t.period_min ?? 1440),
    }));

/**
 * Device reads (catalog capabilities with risk "read", GET …/reads): pick one, fill the parameters the schema declares,
 * read the device now and see its decoded answer as a form. When configuration writes are granted to this device and
 * the caller may configure (GET …/writes), the documented-writable fields of the answer can be edited: the changes are
 * listed, Save sends them as normal commands (audit, lifecycle), then the same read runs again and each change is
 * confirmed against the value read back.
 */
export function DeviceReadsCard({
  id,
  canControl,
  protocol,
}: {
  id: string;
  canControl: boolean;
  protocol?: string | undefined;
}) {
  const { t } = useTranslation();
  const label = useCapabilityLabel();
  const field = useFieldLabel();
  const reads = useQuery({
    queryKey: ['device', id, 'reads'],
    queryFn: () => request('GET', `/api/v1/devices/${id}/reads`, undefined, (v) => api.DeviceReads.parse(v)),
    enabled: canControl,
    staleTime: 10 * 60_000,
  });
  const writesQ = useQuery({
    queryKey: ['device', id, 'writes'],
    queryFn: () =>
      request('GET', `/api/v1/devices/${id}/writes`, undefined, (v) => api.DeviceWrites.parse(v)),
    enabled: canControl,
    staleTime: 10 * 60_000,
  });
  const writes = useMemo(() => new Set((writesQ.data?.items ?? []).map((i) => i.key)), [writesQ.data]);
  const items = (reads.data?.items ?? []).filter((i) => !QUICK.has(i.key));
  const [key, setKey] = useState('');
  const [params, setParams] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [last, setLast] = useState<Last | null>(null);
  const [draft, setDraft] = useState<Obj | null>(null);
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  if (!canControl || !items.length) return null;
  const selected = items.find((i) => i.key === key) ?? items[0]!;
  const schema = selected.params_schema as Schema;
  const props = Object.entries(schema.properties ?? {});

  const query = (queryKey: string, body: Record<string, unknown>) =>
    request('POST', `/api/v1/devices/${id}/queries`, { query_key: queryKey, params: body }, (v) =>
      api.DeviceQueryResponse.parse(v),
    );

  const run = async () => {
    const body: Record<string, unknown> = {};
    for (const [name, p] of props) {
      const raw = params[name];
      if (raw === undefined || raw === '') continue;
      body[name] = p.type === 'integer' || p.type === 'number' ? Number(raw) : raw;
    }
    setBusy(true);
    setError(null);
    setLast(null);
    setDraft(null);
    setOutcome(null);
    try {
      setLast({ key: selected.key, params: body, r: await query(selected.key, body) });
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.code : 'NETWORK_ERROR');
    } finally {
      setBusy(false);
    }
  };

  const result = last?.r.result ?? null;
  const original = result ? bodyOf(result) : {};
  const anyEditable =
    !!result &&
    (Object.entries(original).some(([k, v]) => editable(k, v, protocol, writes)) ||
      (Array.isArray(original.keys) && writes.has('report_config.write')) ||
      (Array.isArray(original.tasks) && writes.has('schedule.write')));

  // What the draft changes, by capability.
  const configChanges = draft
    ? changes(original, Object.fromEntries(Object.entries(draft).filter(([k]) => !k.startsWith('__'))))
    : [];
  const keysDraft = draft?.__keys as string[] | undefined;
  const keysChanged = !!keysDraft && !same(keysDraft, original.keys);
  const tasksDraft = draft?.__tasks as Task[] | undefined;
  const tasksBefore = taskList(original);
  const taskOps: Op[] = [];
  if (tasksDraft) {
    for (const d of tasksDraft) {
      const before = tasksBefore.find((b) => b.task_no === d.task_no);
      if (!before || !same(before as unknown as Json, d as unknown as Json))
        taskOps.push({ capability: 'schedule.write', params: { ...d } });
    }
    for (const b of tasksBefore)
      if (!tasksDraft.some((d) => d.task_no === b.task_no))
        taskOps.push({ capability: 'schedule.delete', params: { task_no: b.task_no } });
  }
  const invalid =
    configChanges.some((c) => validate(c.key, c.to, protocol)) ||
    (!!keysDraft && (keysDraft.length < 1 || keysDraft.length > REPORT_LIST_MAX)) ||
    (tasksDraft ?? []).some(
      (d) =>
        !/^\d\d:\d\d$/.test(d.time) ||
        !(d.dimming >= 0 && d.dimming <= 100) ||
        !(d.period_min >= 1 && d.period_min <= 65535) ||
        d.task_no < 1 ||
        d.task_no > TASK_SLOTS,
    );
  const pending = configChanges.length + (keysChanged ? 1 : 0) + taskOps.length;

  const save = async () => {
    if (!last || !draft || !pending || invalid) return;
    const ops: Op[] = [];
    if (configChanges.length)
      ops.push({
        capability: 'config.write',
        params: {
          settings: Object.fromEntries(configChanges.map((c) => [c.key, toWire(c.key, c.to, protocol)])),
        },
      });
    if (keysChanged)
      ops.push({
        capability: 'report_config.write',
        params: { listener: last.params.listener ?? 0, keys: keysDraft },
      });
    ops.push(...taskOps);
    setSaving(true);
    setOutcome(null);
    try {
      for (const op of ops) {
        const c = await settled(await sendCommand(id, op.capability, op.params));
        if (c.status !== 'SUCCESS') {
          setOutcome({ failed: { capability: op.capability, code: c.errorCode ?? c.status }, checks: [] });
          return;
        }
      }
      // Read-back: the same read again; each change is confirmed against what the device now reports.
      const r = await query(last.key, last.params);
      const after = r.result ? bodyOf(r.result) : {};
      const checks: Outcome['checks'] = configChanges.map((c) => ({
        key: c.key,
        ok: same(after[c.key], c.to),
        read: after[c.key] ?? null,
      }));
      if (keysChanged)
        checks.push({ key: 'keys', ok: same(after.keys, keysDraft!), read: after.keys ?? null });
      if (tasksDraft) {
        const sort = (l: Task[]) => [...l].sort((a, b) => a.task_no - b.task_no) as unknown as Json;
        checks.push({ key: 'tasks', ok: same(sort(taskList(after)), sort(tasksDraft)), read: null });
      }
      setLast({ ...last, r });
      setDraft(null);
      setOutcome({ checks });
    } catch (e) {
      setOutcome({
        failed: { capability: 'read-back', code: e instanceof ApiRequestError ? e.code : 'NETWORK_ERROR' },
        checks: [],
      });
    } finally {
      setSaving(false);
    }
  };

  const edit: EditState | null = draft
    ? { protocol, writes, draft, set: (k, v) => setDraft((d) => ({ ...(d ?? {}), [k]: v })) }
    : null;
  const code = last?.r.error_code ?? error;
  const allOk = !!outcome && !outcome.failed && outcome.checks.every((c) => c.ok);

  return (
    <Card title={t('devices.reads.title')}>
      <p className="muted small" style={{ marginTop: 0 }}>
        {t('devices.reads.hint')}
      </p>
      <div className="toolbar" style={{ flexWrap: 'wrap', alignItems: 'end' }}>
        <label className="field">
          <span className="small muted">{t('devices.reads.read')}</span>
          <select
            className="input"
            value={selected.key}
            disabled={!!draft}
            onChange={(e) => {
              setKey(e.target.value);
              setParams({});
              setLast(null);
              setDraft(null);
              setOutcome(null);
            }}
          >
            {items.map((i) => (
              <option key={i.key} value={i.key}>
                {label(i.key, i.name_en)}
              </option>
            ))}
          </select>
        </label>
        {props.map(([name, p]) => {
          const required = schema.required?.includes(name);
          const title = `${t(`devices.reads.param.${name}` as 'devices.reads.param.group', { defaultValue: name })}${required ? ' *' : ''}`;
          return (
            <label key={name} className="field">
              <span className="small muted">{title}</span>
              {p.enum ? (
                <select
                  className="input"
                  value={params[name] ?? ''}
                  disabled={!!draft}
                  onChange={(e) => setParams({ ...params, [name]: e.target.value })}
                >
                  {!required && <option value="">—</option>}
                  {p.enum.map((v) => (
                    <option key={String(v)} value={String(v)}>
                      {t(`devices.reads.option.${String(v)}` as 'devices.reads.option.clock', {
                        defaultValue: String(v),
                      })}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="input"
                  type={p.type === 'integer' || p.type === 'number' ? 'number' : 'text'}
                  min={p.minimum}
                  max={p.maximum}
                  style={{ width: 110 }}
                  value={params[name] ?? ''}
                  disabled={!!draft}
                  onChange={(e) => setParams({ ...params, [name]: e.target.value })}
                />
              )}
            </label>
          );
        })}
        <button type="button" className="btn" disabled={busy || !!draft} onClick={() => void run()}>
          {busy ? <Loader2 size={14} className="spin" /> : null}
          {t('devices.reads.run')}
        </button>
        {anyEditable && !draft && last?.r.ok && (
          <button
            type="button"
            className="btn"
            onClick={() => {
              setDraft({});
              setOutcome(null);
            }}
          >
            <Pencil size={14} /> {t('devices.edit.start')}
          </button>
        )}
      </div>
      {(last || error) && (
        <p className={`cmd-status ${last?.r.ok ? 'ok' : 'failed'}`} role="status">
          {last?.r.ok ? <CircleCheck size={14} /> : <CircleAlert size={14} />}{' '}
          {last?.r.ok
            ? t('devices.reads.ok', { ms: last.r.duration_ms })
            : t(`devices.query.error.${code ?? 'default'}` as 'devices.query.error.default', {
                defaultValue: t('devices.query.error.default', { code }),
              })}
        </p>
      )}
      {draft && (
        <div className="ro-editbar" role="region" aria-label={t('devices.edit.changes')}>
          <strong>{t('devices.edit.changes')}</strong>
          {pending === 0 ? (
            <span className="muted small">{t('devices.edit.noChanges')}</span>
          ) : (
            <ul className="ro-changes">
              {configChanges.map((c) => (
                <li key={c.key}>
                  <span>{field(c.key)}</span>
                  <span className="ro-change">
                    <ValueView k={c.key} v={c.from} /> → <ValueView k={c.key} v={c.to} />
                  </span>
                </li>
              ))}
              {keysChanged && (
                <li>
                  <span>{field('keys')}</span>
                  <span className="ro-change">{t('devices.edit.listChange', { n: keysDraft!.length })}</span>
                </li>
              )}
              {taskOps.map((o, i) => (
                <li key={i}>
                  <span>{t('devices.edit.taskNo', { n: Number(o.params.task_no) })}</span>
                  <span className="ro-change">
                    {o.capability === 'schedule.delete'
                      ? t('devices.edit.taskDelete')
                      : t('devices.edit.taskSet', {
                          time: String(o.params.time),
                          dimming: Number(o.params.dimming),
                          period: Number(o.params.period_min),
                        })}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <span className="toolbar">
            <button
              type="button"
              className="btn btn-primary"
              disabled={saving || pending === 0 || invalid}
              onClick={() => void save()}
            >
              {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} {t('devices.edit.save')}
            </button>
            <button type="button" className="btn" disabled={saving} onClick={() => setDraft(null)}>
              <X size={14} /> {t('devices.edit.cancel')}
            </button>
            {invalid && <span className="ro-error">{t('devices.edit.fix')}</span>}
          </span>
        </div>
      )}
      {outcome && (
        <div className="ro-outcome" role="status">
          {outcome.failed ? (
            <p className="cmd-status failed">
              <CircleAlert size={14} />{' '}
              {t('devices.edit.failed', {
                what: label(outcome.failed.capability),
                code: outcome.failed.code,
              })}
            </p>
          ) : (
            <>
              <p className={`cmd-status ${allOk ? 'ok' : 'failed'}`}>
                {allOk ? <CircleCheck size={14} /> : <CircleAlert size={14} />}{' '}
                {allOk ? t('devices.edit.confirmed') : t('devices.edit.mismatch')}
              </p>
              <ul className="ro-changes">
                {outcome.checks.map((c) => (
                  <li key={c.key}>
                    <span>{field(c.key)}</span>
                    <span className="ro-change">
                      {c.ok ? (
                        <span className="ro-alarm">
                          <CircleCheck size={13} /> {t('devices.edit.readBackOk')}
                        </span>
                      ) : (
                        <>
                          <span className="ro-alarm is-active">
                            <CircleAlert size={13} /> {t('devices.edit.readBackDiff')}
                          </span>{' '}
                          {c.key === 'keys' || c.key === 'tasks' ? null : <ValueView k={c.key} v={c.read} />}
                        </>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {result && <ReadResult result={result} edit={edit} />}
    </Card>
  );
}
